import { FrontendUserSchema } from '@buyerflo/core/schemas/userMembership'
import { Badge } from '@buyerflo/ui/components/Badge'
import { ChevronRight } from '@tamagui/lucide-icons-2'
import { useConjunctionalAgenciesApi, useRolesApi, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import Loading from 'components/Loading'
import { Search } from 'components/icons'
import Select, { Option } from 'components/select'
import { RadioChips } from '@buyerflo/ui/components/RadioChips'
import { sortBy } from 'ramda'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Avatar, Input, SizableText, Text, View, XStack, YStack } from 'tamagui'

type StatusFilter = 'all' | 'active' | 'pending'

const columnFlex = { role: 1.2, agency: 1.3, contact: 1, status: 0.9 } as const

const UserAvatar = ({ user, size = 34 }: { user: FrontendUserSchema; size?: number }) => (
    <Avatar
        size={size}
        circular
    >
        {user.profileImage ? (
            <Avatar.Image src={user.profileImage} />
        ) : (
            <Avatar.Fallback
                backgroundColor="$primaryMuted"
                alignItems="center"
                justifyContent="center"
            >
                <SizableText
                    fontSize={size * 0.36}
                    fontWeight="600"
                >
                    {`${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`}
                </SizableText>
            </Avatar.Fallback>
        )}
    </Avatar>
)

const StatusPill = ({ active }: { active: boolean }) =>
    active ? (
        <Badge
            tone="ok"
            dot
        >
            Active
        </Badge>
    ) : (
        <Badge
            tone="warn"
            dot
        >
            Pending
        </Badge>
    )

const HeaderCell = ({ children, flex }: { children: React.ReactNode; flex: number }) => (
    <View
        flex={flex}
        paddingHorizontal="$2"
    >
        <Text
            fontWeight="600"
            fontSize="$2"
            color="$colorMuted"
            textTransform="uppercase"
            letterSpacing={0.5}
        >
            {children}
        </Text>
    </View>
)

const Muted = ({ children, mono }: { children: React.ReactNode; mono?: boolean }) => (
    <Text
        fontSize="$2"
        color="$colorSoft"
        fontFamily={mono ? '$mono' : undefined}
        numberOfLines={1}
    >
        {children}
    </Text>
)

export default function MembersList() {
    const usersApi = useUsersApi()
    const rolesApi = useRolesApi()
    const conjunctionalAgenciesApi = useConjunctionalAgenciesApi()
    const { user: userData } = useUserData()
    const navigate = useNavigate()

    const [search, setSearch] = useState('')
    const [roleFilter, setRoleFilter] = useState<string>('all')
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

    const canViewAll = userData.permissions.includes('users.view')

    const roleName = (user: FrontendUserSchema) => {
        if (user.superUser) return 'Super User'
        return rolesApi.data?.find(role => role.id === user.roleId)?.roleName ?? '—'
    }

    const agencyName = (user: FrontendUserSchema) => {
        const conjId = user.conjunctionalAgencyId ?? user.salesAssistantConjunctionalAgencyId
        if (!conjId) return undefined
        return (conjunctionalAgenciesApi.data as { conjunctionalAgencyId: string; licenseeName?: string }[] | undefined)?.find(
            agency => agency.conjunctionalAgencyId === conjId,
        )?.licenseeName
    }

    const base = useMemo(() => {
        const all = usersApi.data ?? []
        if (canViewAll) return all
        return all.filter(
            user =>
                user.userId === userData.userId ||
                (userData.salesAssistantLeadAgentId && userData.salesAssistantLeadAgentId === user.userId) ||
                (userData.salesAssistantConjunctionalAgencyId &&
                    userData.salesAssistantConjunctionalAgencyId === user.conjunctionalAgencyId),
        )
    }, [usersApi.data, canViewAll, userData])

    const counts = useMemo(
        () => ({
            all: base.length,
            active: base.filter(u => u.active).length,
            pending: base.filter(u => !u.active).length,
        }),
        [base],
    )

    const filtered = useMemo(() => {
        const result = base.filter(user => {
            if (statusFilter === 'active' && !user.active) return false
            if (statusFilter === 'pending' && user.active) return false
            if (roleFilter !== 'all' && user.roleId !== roleFilter) return false
            if (search) {
                const hay = `${user.firstName} ${user.lastName} ${user.email} ${roleName(user)} ${
                    agencyName(user) ?? ''
                }`.toLowerCase()
                if (!hay.includes(search.toLowerCase())) return false
            }
            return true
        })
        return sortBy(u => `${u.lastName} ${u.firstName}`.toLowerCase(), result)
    }, [base, statusFilter, roleFilter, search, rolesApi.data, conjunctionalAgenciesApi.data])

    if (usersApi.data === undefined) {
        return <Loading />
    }

    const goToUser = (userId: string) => navigate(userId)

    return (
        <YStack gap="$3">
            {/* Filter bar */}
            {canViewAll && (
                <YStack
                    backgroundColor="$backgroundStrong"
                    borderWidth={1}
                    borderColor="$borderColor"
                    borderRadius={14}
                    padding="$3"
                    data-help-id="users-filters"
                >
                    <XStack
                        gap="$3"
                        flexWrap="wrap"
                        alignItems="center"
                    >
                        <XStack
                            flex={1}
                            minWidth={220}
                            alignItems="center"
                            gap="$2"
                            backgroundColor="$background"
                            borderWidth={1}
                            borderColor="$borderColor"
                            borderRadius="$4"
                            paddingHorizontal="$3"
                            height={40}
                        >
                            <Search
                                size={15}
                                color="$colorMuted"
                            />
                            <Input
                                unstyled
                                flex={1}
                                fontSize={14}
                                color="$color"
                                placeholder="Search by name, email or role…"
                                placeholderTextColor="$colorFaint"
                                value={search}
                                onChangeText={setSearch}
                            />
                        </XStack>
                        <View
                            width={190}
                            data-help-id="users-role-filter"
                        >
                            <Select
                                value={roleFilter}
                                onChange={(_e, val) => setRoleFilter(val ?? 'all')}
                            >
                                <Option value="all">All roles</Option>
                                {sortBy(r => r.roleName.toLowerCase(), rolesApi.data ?? []).map(role => (
                                    <Option
                                        key={role.id}
                                        value={role.id}
                                    >
                                        {role.roleName}
                                    </Option>
                                ))}
                            </Select>
                        </View>
                        <View data-help-id="users-status-filter">
                            <RadioChips<StatusFilter>
                                value={statusFilter}
                                onChange={setStatusFilter}
                                options={[
                                    { value: 'all', label: `All ${counts.all}` },
                                    { value: 'active', label: `Active ${counts.active}` },
                                    { value: 'pending', label: `Pending ${counts.pending}` },
                                ]}
                            />
                        </View>
                    </XStack>
                </YStack>
            )}

            {/* Desktop table */}
            <YStack
                display="none"
                $sm={{ display: 'flex' }}
                backgroundColor="$backgroundStrong"
                borderWidth={1}
                borderColor="$borderColor"
                borderRadius={14}
                overflow="hidden"
                data-help-id="users-list"
            >
                <XStack
                    backgroundColor="$background"
                    borderBottomWidth={1}
                    borderBottomColor="$borderColor"
                    paddingVertical="$3"
                    paddingHorizontal="$4"
                >
                    <HeaderCell flex={2}>Name</HeaderCell>
                    <HeaderCell flex={columnFlex.role}>Role</HeaderCell>
                    <HeaderCell flex={columnFlex.agency}>Agency</HeaderCell>
                    <HeaderCell flex={columnFlex.contact}>Contact</HeaderCell>
                    <HeaderCell flex={columnFlex.status}>Status</HeaderCell>
                    <View width={28} />
                </XStack>

                {filtered.map((user, index) => (
                    <XStack
                        key={user.userId}
                        paddingVertical="$3"
                        paddingHorizontal="$4"
                        alignItems="center"
                        cursor="pointer"
                        animation="quick"
                        hoverStyle={{ backgroundColor: '$backgroundHover' }}
                        pressStyle={{ backgroundColor: '$backgroundPress' }}
                        borderBottomWidth={index < filtered.length - 1 ? 1 : 0}
                        borderBottomColor="$borderColor"
                        onPress={() => goToUser(user.userId)}
                        {...(user.userId === userData.userId ? { 'data-tour': 'current-user-row' } : {})}
                    >
                        <XStack
                            flex={2}
                            paddingHorizontal="$2"
                            gap="$3"
                            alignItems="center"
                            minWidth={0}
                        >
                            <UserAvatar user={user} />
                            <YStack
                                flex={1}
                                minWidth={0}
                            >
                                <Text
                                    fontSize="$3"
                                    fontWeight="600"
                                    numberOfLines={1}
                                >
                                    {user.firstName} {user.lastName}
                                </Text>
                                <Text
                                    fontSize="$1"
                                    color="$colorMuted"
                                    numberOfLines={1}
                                >
                                    {user.email}
                                </Text>
                            </YStack>
                        </XStack>
                        <View
                            flex={columnFlex.role}
                            paddingHorizontal="$2"
                            overflow="hidden"
                        >
                            <Muted>{roleName(user)}</Muted>
                        </View>
                        <View
                            flex={columnFlex.agency}
                            paddingHorizontal="$2"
                            overflow="hidden"
                        >
                            <Muted>{agencyName(user) ?? '—'}</Muted>
                        </View>
                        <View
                            flex={columnFlex.contact}
                            paddingHorizontal="$2"
                            overflow="hidden"
                        >
                            <Muted mono>{user.mobileNumber || '—'}</Muted>
                        </View>
                        <View
                            flex={columnFlex.status}
                            paddingHorizontal="$2"
                        >
                            <StatusPill active={user.active} />
                        </View>
                        <View
                            width={28}
                            alignItems="center"
                        >
                            <ChevronRight
                                size={16}
                                color="$colorFaint"
                            />
                        </View>
                    </XStack>
                ))}

                {filtered.length === 0 && (
                    <View
                        padding="$8"
                        alignItems="center"
                    >
                        <Muted>No users match your filters.</Muted>
                    </View>
                )}
            </YStack>

            {/* Mobile cards */}
            <YStack
                display="flex"
                $sm={{ display: 'none' }}
                gap="$2"
            >
                {filtered.map(user => (
                    <XStack
                        key={user.userId}
                        gap="$3"
                        alignItems="center"
                        padding="$3"
                        borderWidth={1}
                        borderColor="$borderColor"
                        borderRadius="$4"
                        backgroundColor="$backgroundStrong"
                        cursor="pointer"
                        animation="quick"
                        hoverStyle={{ backgroundColor: '$backgroundHover' }}
                        pressStyle={{ backgroundColor: '$backgroundPress' }}
                        onPress={() => goToUser(user.userId)}
                    >
                        <UserAvatar user={user} />
                        <YStack
                            flex={1}
                            minWidth={0}
                            gap="$0.5"
                        >
                            <Text
                                fontSize="$3"
                                fontWeight="600"
                                numberOfLines={1}
                            >
                                {user.firstName} {user.lastName}
                            </Text>
                            <Text
                                fontSize="$1"
                                color="$colorMuted"
                                numberOfLines={1}
                            >
                                {roleName(user)}
                                {agencyName(user) ? ` · ${agencyName(user)}` : ''}
                            </Text>
                        </YStack>
                        <StatusPill active={user.active} />
                    </XStack>
                ))}
                {filtered.length === 0 && (
                    <View
                        padding="$6"
                        alignItems="center"
                    >
                        <Muted>No users match your filters.</Muted>
                    </View>
                )}
            </YStack>
        </YStack>
    )
}
